import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import { 
  Building2, 
  FlaskConical, 
  BookOpen, 
  Trophy, 
  Palette, 
  Save, 
  Upload, 
  Plus, 
  Trash2
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const DEFAULT_FACILITIES_DATA = {
  classrooms: {
    title: 'Classrooms',
    subtitle: 'Bright, Tech-Ready Learning Spaces',
    description: 'Our classrooms are designed to inspire curiosity and support collaborative learning with modern technology and flexible seating arrangements.',
    color: '#3b82f6',
    colorLight: '#dbeafe',
    icon: 'fa-chalkboard-user',
    images: ['/assets/news/news3.jpg', '/assets/news/news5.jpg', '/assets/news/news6.jpg'],
    features: [
      { icon: 'fa-laptop', title: 'Interactive Displays', detail: 'Smart boards and projectors for engaging multimedia lessons' },
      { icon: 'fa-chair', title: 'Flexible Seating', detail: 'Modular furniture that adapts to different learning activities' },
      { icon: 'fa-wifi', title: 'High-Speed Internet', detail: 'Reliable connectivity for research and online learning' },
      { icon: 'fa-lightbulb', title: 'Natural Lighting', detail: 'Large windows for natural light and student wellbeing' }
    ],
    highlights: [
      'Capacity: 25-30 students per classroom',
      'Air-conditioned and climate-controlled',
      'Accessible for students with special needs',
      'Equipped with first aid and safety equipment'
    ],
    content: 'Our classrooms represent a commitment to creating optimal learning environments. Each room is thoughtfully designed with student comfort and engagement in mind. Teachers have access to the latest educational technology, while students benefit from ergonomic furniture and abundant natural light. The flexible layout allows for various teaching methodologies, from traditional lectures to collaborative group work and project-based learning.'
  },
  labs: {
    title: 'Science & ICT Labs',
    subtitle: 'Hands-On Learning & Innovation',
    description: 'State-of-the-art laboratories where students conduct experiments, code, and explore scientific concepts through practical inquiry.',
    color: '#10b981',
    colorLight: '#d1fae5',
    icon: 'fa-flask',
    images: ['/assets/news/news5.jpg', '/assets/news/news6.jpg', '/assets/news/news8.jpg'],
    features: [
      { icon: 'fa-flask', title: 'Science Lab', detail: 'Equipped for chemistry, physics, and biology experiments' },
      { icon: 'fa-code', title: 'Coding Lab', detail: 'Computers and software for programming and digital literacy' },
      { icon: 'fa-robot', title: 'Robotics Station', detail: 'Equipment for robotics projects and STEM challenges' },
      { icon: 'fa-microscope', title: 'Research Tools', detail: 'Microscopes, sensors, and data collection equipment' }
    ],
    highlights: [
      'Safety protocols and protective equipment provided',
      'Lab technician on duty during all sessions',
      'Regular maintenance and equipment updates',
      'Capacity: 20-25 students per session'
    ],
    content: 'Our labs are designed to transform theoretical knowledge into practical understanding. Students engage in hands-on experiments that reinforce classroom learning and develop critical thinking skills. The science lab features modern equipment for chemistry, physics, and biology investigations. The ICT lab provides access to computers, coding platforms, and robotics kits, preparing students for careers in technology and innovation. All labs maintain strict safety standards and are supervised by trained technicians.'
  },
  library: {
    title: 'Library & Learning Commons',
    subtitle: 'Knowledge Hub & Quiet Study Space',
    description: 'A welcoming sanctuary for reading, research, and collaborative learning with curated collections and digital resources.',
    color: '#8b5cf6',
    colorLight: '#ede9fe',
    icon: 'fa-book',
    images: ['/assets/news/news6.jpg', '/assets/news/news3.jpg', '/assets/news/news5.jpg'],
    features: [
      { icon: 'fa-book', title: 'Extensive Collection', detail: '5,000+ books across all subjects and reading levels' },
      { icon: 'fa-computer', title: 'Digital Resources', detail: 'Online databases, e-books, and research platforms' },
      { icon: 'fa-users', title: 'Study Groups', detail: 'Collaborative spaces for group projects and discussions' },
      { icon: 'fa-headphones', title: 'Media Center', detail: 'Audio-visual resources and multimedia equipment' }
    ],
    highlights: [
      'Quiet study areas and collaborative zones',
      'Librarian support for research guidance',
      'Extended hours for student access',
      'Comfortable seating and reading nooks'
    ],
    content: 'The library serves as the intellectual heart of our school. Beyond traditional book collections, we offer digital resources, research databases, and multimedia materials. Students can access quiet study areas for focused work or collaborative spaces for group projects. Our librarian provides research guidance and helps students develop information literacy skills. The library is designed to foster a love of reading and support academic excellence across all disciplines.'
  },
  'sports-field': {
    title: 'Sports Field & Athletic Facilities',
    subtitle: 'Excellence in Physical Education',
    description: 'Multi-purpose courts and fields designed for athletics, team sports, and comprehensive physical education programs.',
    color: '#f59e0b',
    colorLight: '#fef3c7',
    icon: 'fa-futbol',
    images: ['/assets/news/news8.jpg', '/assets/news/news5.jpg', '/assets/news/news3.jpg'],
    features: [
      { icon: 'fa-futbol', title: 'Soccer Field', detail: 'Full-size field with professional-grade turf' },
      { icon: 'fa-basketball', title: 'Basketball Courts', detail: 'Indoor and outdoor courts with proper markings' },
      { icon: 'fa-dumbbell', title: 'Fitness Area', detail: 'Equipment for strength training and conditioning' },
      { icon: 'fa-person-running', title: 'Track & Field', detail: 'Running track and field event areas' }
    ],
    highlights: [
      'Professional coaching staff',
      'Safety equipment and first aid stations',
      'Lighting for evening activities',
      'Spectator seating for events'
    ],
    content: 'Our sports facilities support a comprehensive athletic program that promotes physical fitness, teamwork, and sportsmanship. The multi-purpose field accommodates soccer, field hockey, and other sports. Indoor courts provide year-round access to basketball and volleyball. Professional coaches guide students through training sessions and competitive events. All facilities meet international standards and are maintained to the highest safety specifications. We believe in developing well-rounded students who value health and active lifestyles.'
  },
  'art-music-rooms': {
    title: 'Art & Music Studios',
    subtitle: 'Creative Expression & Performance',
    description: 'Dedicated creative spaces where students explore visual arts, music, and performance with professional-grade equipment.',
    color: '#ec4899',
    colorLight: '#fce7f3',
    icon: 'fa-palette',
    images: ['/assets/news/news3.jpg', '/assets/news/news6.jpg', '/assets/news/news8.jpg'],
    features: [
      { icon: 'fa-palette', title: 'Art Studio', detail: 'Painting, drawing, sculpture, and mixed media spaces' },
      { icon: 'fa-music', title: 'Music Rooms', detail: 'Practice rooms and ensemble rehearsal spaces' },
      { icon: 'fa-microphone', title: 'Performance Stage', detail: 'Theater with sound system and lighting' },
      { icon: 'fa-camera', title: 'Media Lab', detail: 'Video production and photography equipment' }
    ],
    highlights: [
      'Professional-grade instruments and materials',
      'Experienced art and music instructors',
      'Regular performances and exhibitions',
      'Access to digital creative tools'
    ],
    content: 'Our art and music studios nurture creative talent and self-expression. The art studio provides space for painting, drawing, sculpture, and mixed media projects. Music rooms are equipped with pianos, guitars, drums, and other instruments for individual practice and ensemble rehearsals. The performance stage hosts student concerts, plays, and showcases throughout the year. We believe that arts education develops critical thinking, emotional intelligence, and cultural appreciation. Students have opportunities to exhibit their work and perform for the school community.'
  }
};

const FACILITIES_LIST = [
  { id: 'classrooms', name: 'Classrooms', icon: Building2, color: '#3b82f6' },
  { id: 'labs', name: 'Science & ICT Labs', icon: FlaskConical, color: '#10b981' },
  { id: 'library', name: 'Library & Commons', icon: BookOpen, color: '#8b5cf6' },
  { id: 'sports-field', name: 'Sports Field', icon: Trophy, color: '#f59e0b' },
  { id: 'art-music-rooms', name: 'Art & Music Studios', icon: Palette, color: '#ec4899' }
];

export default function FacilitiesOverviewDetailForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [selectedFacilityId, setSelectedFacilityId] = useState('classrooms');
  const [facilitiesData, setFacilitiesData] = useState(DEFAULT_FACILITIES_DATA);
  const [originalData, setOriginalData] = useState(DEFAULT_FACILITIES_DATA);
  const [pendingFields, setPendingFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [sectionId, setSectionId] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [imageUploading, setImageUploading] = useState(false);

  const getAuthHeaders = useCallback(() => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }, []);

  const fetchMediaMap = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const json = await res.json();
        const items = json.data?.items || json.data || [];
        if (Array.isArray(items)) {
          const map = {};
          items.forEach(m => {
            if (m.id && m.file_path) {
              map[m.id] = m.file_path;
              map[String(m.id)] = m.file_path;
            }
          });
          setMediaMap(map);
        }
      }
    } catch (e) {
      console.warn('[FacilitiesOverviewDetailForm] Media fetch error:', e);
    }
  }, [apiBase, getAuthHeaders]);

  const resolveImage = useCallback((imgSrc) => {
    if (!imgSrc) return null;
    const str = String(imgSrc).trim();
    if (!str) return null;
    if (mediaMap[str]) return mediaMap[str];
    const num = Number(str);
    if (!isNaN(num) && mediaMap[num]) return mediaMap[num];
    const resolved = resolveMediaSrc(str, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://') || resolved.startsWith('blob:') || resolved.startsWith('data:')) {
        return resolved;
      }
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    }
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${str}`;
    }
    return str;
  }, [mediaMap, apiBase]);

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=facilities`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'facilities-overview-details' || s.section_type === 'facility-details');
      }

      if (foundSection) {
        setSectionId(foundSection.id);
        let rawContent = foundSection.content_published || foundSection.content;
        if (typeof rawContent === 'string') {
          try {
            rawContent = JSON.parse(rawContent);
          } catch (e) {
            rawContent = {};
          }
        }

        const dataEn = rawContent?.en || rawContent || {};
        const merged = { ...DEFAULT_FACILITIES_DATA };
        Object.keys(DEFAULT_FACILITIES_DATA).forEach(key => {
          if (dataEn[key]) {
            merged[key] = {
              ...DEFAULT_FACILITIES_DATA[key],
              ...dataEn[key]
            };
          }
        });

        setFacilitiesData(merged);
        setOriginalData(merged);

        try {
          const pendRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendRes.ok) {
            const pendJson = await pendRes.json();
            if (pendJson.success && Array.isArray(pendJson.data?.pending_changes)) {
              const pendMap = {};
              pendJson.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  pendMap[c.field_id] = true;
                }
              });
              setPendingFields(pendMap);
            }
          }
        } catch (pe) {
          console.warn('[FacilitiesOverviewDetailForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[FacilitiesOverviewDetailForm] Load error:', err);
      toast.error('Gagal memuat data Facilities Overview Details');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    fetchMediaMap();
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const currentFacility = facilitiesData[selectedFacilityId] || DEFAULT_FACILITIES_DATA[selectedFacilityId];

  const handleFacilityFieldChange = (field, value) => {
    setFacilitiesData(prev => ({
      ...prev,
      [selectedFacilityId]: {
        ...prev[selectedFacilityId],
        [field]: value
      }
    }));
  };

  const handleFeatureChange = (index, field, value) => {
    setFacilitiesData(prev => {
      const updatedFeatures = [...(prev[selectedFacilityId].features || [])];
      updatedFeatures[index] = { ...updatedFeatures[index], [field]: value };
      return {
        ...prev,
        [selectedFacilityId]: {
          ...prev[selectedFacilityId],
          features: updatedFeatures
        }
      };
    });
  };

  const handleAddFeature = () => {
    setFacilitiesData(prev => ({
      ...prev,
      [selectedFacilityId]: {
        ...prev[selectedFacilityId],
        features: [
          ...(prev[selectedFacilityId].features || []),
          { icon: 'fa-star', title: 'Fitur Baru', detail: 'Keterangan fitur fasilitas...' }
        ]
      }
    }));
  };

  const handleRemoveFeature = (index) => {
    setFacilitiesData(prev => ({
      ...prev,
      [selectedFacilityId]: {
        ...prev[selectedFacilityId],
        features: (prev[selectedFacilityId].features || []).filter((_, i) => i !== index)
      }
    }));
  };

  const handleHighlightChange = (index, value) => {
    setFacilitiesData(prev => {
      const updatedHighlights = [...(prev[selectedFacilityId].highlights || [])];
      updatedHighlights[index] = value;
      return {
        ...prev,
        [selectedFacilityId]: {
          ...prev[selectedFacilityId],
          highlights: updatedHighlights
        }
      };
    });
  };

  const handleAddHighlight = () => {
    setFacilitiesData(prev => ({
      ...prev,
      [selectedFacilityId]: {
        ...prev[selectedFacilityId],
        highlights: [...(prev[selectedFacilityId].highlights || []), 'Poin sorotan fasilitas baru...']
      }
    }));
  };

  const handleRemoveHighlight = (index) => {
    setFacilitiesData(prev => ({
      ...prev,
      [selectedFacilityId]: {
        ...prev[selectedFacilityId],
        highlights: (prev[selectedFacilityId].highlights || []).filter((_, i) => i !== index)
      }
    }));
  };

  const handleImageReplace = async (imgIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran gambar maksimal 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Format gambar harus JPG, PNG, atau WEBP');
      return;
    }

    setImageUploading(true);
    try {
      const data = new FormData();
      data.append('file', file);
      data.append('type', 'image');

      let token = localStorage.getItem('token') || localStorage.getItem('ph_auth_token') || sessionStorage.getItem('token');
      const res = await fetch(`${apiBase}/homepage/cms/media`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: data
      });

      const json = await res.json();
      if (json.ok && json.data) {
        const url = json.data.url || json.data.file_path;
        const currentImgs = [...(currentFacility.images || [])];
        currentImgs[imgIndex] = url;
        handleFacilityFieldChange('images', currentImgs);
        toast.success(`Foto slide #${imgIndex + 1} berhasil diperbarui`);
      } else {
        toast.error(json.error || 'Gagal upload foto');
      }
    } catch (e) {
      console.error('[FacilitiesOverviewDetailForm] Image upload error:', e);
      toast.error('Gagal upload: ' + e.message);
    } finally {
      setImageUploading(false);
    }
  };

  const submitFacilitySection = async () => {
    if (!sectionId) {
      toast.error('Section ID belum terdaftar di database.');
      return;
    }

    const fieldKey = selectedFacilityId;
    setSubmitting(prev => ({ ...prev, [fieldKey]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldKey,
          field_name: `Facility Detail: ${currentFacility.title}`,
          old_value: originalData[fieldKey],
          new_value: facilitiesData[fieldKey],
          locale: 'en'
        })
      });

      const json = await res.json();
      if (json.success || json.ok) {
        toast.success(`Data detail ${currentFacility.title} berhasil diajukan untuk review`);
        setPendingFields(prev => ({ ...prev, [fieldKey]: true }));
      } else {
        toast.error(json.error || json.message || 'Gagal mengajukan perubahan');
      }
    } catch (e) {
      console.error('[FacilitiesOverviewDetailForm] Submit error:', e);
      toast.error('Terjadi kesalahan: ' + e.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-spinner" />
        <p>Memuat data Detail Campus Overview...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-wrapper">
      <ToastContainer />

      {/* Top Banner */}
      <div className="unified-header-card">
        <div className="header-info">
          <h2>Campus Overview Detail Pages</h2>
          <p>Kelola konten lengkap detail page untuk masing-masing 5 Fasilitas: judul, galeri slide, fitur spesifik, sorotan utama, dan ulasan lengkap.</p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="unified-btn-primary"
            onClick={submitFacilitySection}
            disabled={submitting[selectedFacilityId]}
          >
            <Save size={16} />
            <span>{submitting[selectedFacilityId] ? 'Menyimpan...' : `Simpan Detail ${currentFacility.title}`}</span>
          </button>
        </div>
      </div>

      {/* Facility Selector Pills */}
      <div className="unified-tab-bar sub-tabs-bar">
        {FACILITIES_LIST.map(f => {
          const Icon = f.icon;
          const isActive = f.id === selectedFacilityId;
          return (
            <button
              key={f.id}
              type="button"
              className={`tab-pill-btn ${isActive ? 'active' : ''}`}
              onClick={() => setSelectedFacilityId(f.id)}
            >
              <Icon size={16} className="w-4 h-4 mr-2" style={{ color: f.color }} />
              <span>{f.name}</span>
            </button>
          );
        })}
      </div>

      {/* Selected Facility Card */}
      <div className="unified-section-card">
        <div className="card-header" style={{ borderLeft: `6px solid ${currentFacility.color}` }}>
          <div className="title-with-icon">
            <Building2 size={20} style={{ color: currentFacility.color }} />
            <h3>{currentFacility.title} - Informasi Detail</h3>
          </div>
          {pendingFields[selectedFacilityId] && (
            <span className="field-pending-tag">Pending Review</span>
          )}
        </div>

        <div className="card-body form-grid-2">
          <div className="form-group">
            <label>Facility Title</label>
            <input
              type="text"
              value={currentFacility.title}
              onChange={e => handleFacilityFieldChange('title', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Subtitle / Slogan</label>
            <input
              type="text"
              value={currentFacility.subtitle}
              onChange={e => handleFacilityFieldChange('subtitle', e.target.value)}
              placeholder="Bright, Tech-Ready Learning Spaces"
            />
          </div>

          <div className="form-group">
            <label>Theme Color (Hex)</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={currentFacility.color}
                onChange={e => handleFacilityFieldChange('color', e.target.value)}
                style={{ width: '45px', height: '38px', padding: 0, cursor: 'pointer' }}
              />
              <input
                type="text"
                value={currentFacility.color}
                onChange={e => handleFacilityFieldChange('color', e.target.value)}
                placeholder="#3b82f6"
              />
            </div>
          </div>

          <div className="form-group full-width">
            <label>Short Description (Sub-header)</label>
            <textarea
              rows={2}
              value={currentFacility.description}
              onChange={e => handleFacilityFieldChange('description', e.target.value)}
              placeholder="Deskripsi singkat fasilitas..."
            />
          </div>

          {/* 3 Slider Images */}
          <div className="form-group full-width">
            <label>Detail Carousel Images (3 Foto Slide)</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '8px' }}>
              {(currentFacility.images || []).map((img, idx) => (
                <div key={idx} className="item-sub-card" style={{ padding: '8px', textAlign: 'center' }}>
                  <span className="item-counter" style={{ display: 'block', marginBottom: '6px' }}>Slide #{idx + 1}</span>
                  <div style={{ width: '100%', height: '120px', borderRadius: '6px', overflow: 'hidden', background: '#f1f5f9', marginBottom: '8px' }}>
                    <img src={resolveImage(img)} alt={`Slide ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <label className="btn-upload-unified" style={{ display: 'inline-flex', padding: '6px 12px', fontSize: '12px' }}>
                    <Upload size={12} />
                    <span>Ganti Foto</span>
                    <input type="file" accept="image/*" onChange={e => handleImageReplace(idx, e)} disabled={imageUploading} style={{ display: 'none' }} />
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group full-width">
            <label>Full Content Narrative (Ulasan Lengkap Fasilitas)</label>
            <textarea
              rows={4}
              value={currentFacility.content}
              onChange={e => handleFacilityFieldChange('content', e.target.value)}
              placeholder="Ulasan detail fasilitas dan peruntukannya bagi siswa..."
            />
          </div>
        </div>

        {/* Features & Highlights */}
        <div className="sub-section-divider">
          <h4>Fitur Utama & Poin Sorotan Fasilitas</h4>
        </div>

        <div className="card-body form-grid-2">
          {/* Features */}
          <div className="item-sub-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <span className="item-counter">Spesifikasi Fitur Utama</span>
              <button type="button" className="btn-field-save" onClick={handleAddFeature}>
                <Plus size={14} /> Tambah Fitur
              </button>
            </div>
            {(currentFacility.features || []).map((feat, idx) => (
              <div key={idx} style={{ marginBottom: '12px', padding: '12px', background: '#ffffff', borderRadius: '10px', border: '1.5px solid #e2e8f0' }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    value={feat.title}
                    onChange={e => handleFeatureChange(idx, 'title', e.target.value)}
                    placeholder="Judul Fitur"
                    style={{ flex: 1 }}
                  />
                  <button type="button" className="btn-item-delete" onClick={() => handleRemoveFeature(idx)} title="Hapus Fitur">
                    <Trash2 size={14} />
                  </button>
                </div>
                <input
                  type="text"
                  value={feat.detail}
                  onChange={e => handleFeatureChange(idx, 'detail', e.target.value)}
                  placeholder="Detail penjelasan fitur..."
                />
              </div>
            ))}
          </div>

          {/* Highlights */}
          <div className="item-sub-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <span className="item-counter">Highlights & Kapasitas</span>
              <button type="button" className="btn-field-save" onClick={handleAddHighlight}>
                <Plus size={14} /> Tambah Poin
              </button>
            </div>
            {(currentFacility.highlights || []).map((h, idx) => (
              <div key={idx} className="input-with-action" style={{ marginBottom: '10px' }}>
                <input
                  type="text"
                  value={h}
                  onChange={e => handleHighlightChange(idx, e.target.value)}
                  placeholder="Poin sorotan..."
                />
                <button type="button" className="btn-item-delete" onClick={() => handleRemoveHighlight(idx)} title="Hapus Poin">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
